import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { from, map, take } from 'rxjs';

import { ThemeService } from '@/core/theme';

import { AuthService } from './auth.service';
import { AppRoute, canAccessRoute } from './constants/group-access.constants';
import { UserData } from './interfaces/token-data.interface';

/**
 * Guard that protects routes requiring authentication
 * Redirects to login if user is not authenticated
 */
export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Check authentication status using Amplify
  return from(authService.checkExistingAuthentication()).pipe(
    take(1),
    map((isAuthenticated: boolean) => {
      if (isAuthenticated) {
        console.warn('User is authenticated, allowing access');
        return true;
      } else {
        console.warn('User not authenticated, redirecting to login');

        // Store the attempted URL for redirect after login
        const currentUrl = state.url;
        if (currentUrl !== '/login' && currentUrl !== '/callback') {
          authService.storeRedirectUrl(currentUrl);
        }

        // Redirect to login
        router.navigate(['/login']);
        return false;
      }
    })
  );
};

/**
 * Guard that redirects authenticated users away from login/public pages
 * Useful for login page to prevent authenticated users from seeing it
 */
export const publicGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Check authentication status using Amplify
  return from(authService.checkExistingAuthentication()).pipe(
    take(1),
    map((isAuthenticated: boolean) => {
      if (isAuthenticated) {
        const defaultRoute = authService.getDefaultRoute();
        console.warn('User already authenticated, redirecting to', defaultRoute);
        router.navigate([defaultRoute]);
        return false;
      } else {
        console.warn('User not authenticated, allowing access to public page');
        return true;
      }
    })
  );
};

/**
 * Role-based guard factory
 * Usage: canActivate: [roleGuard(['admin', 'user'])]
 */
export function roleGuard(allowedRoles: string[]): CanActivateFn {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    return authService.getUserData().pipe(
      take(1),
      map((userData: UserData | null) => {
        if (!userData) {
          console.log('No user data found, redirecting to login');
          router.navigate(['/login']);
          return false;
        }

        const userRoles = userData.roles || [];
        const hasRole = userRoles.some((role: string) => allowedRoles.includes(role));

        if (!hasRole) {
          console.log('User does not have required role', { userRoles, allowedRoles });
          router.navigate(['/unauthorized']);
          return false;
        }

        console.log('User has required role, allowing access', { userRoles, allowedRoles });
        return true;
      })
    );
  };
}

/**
 * Cognito-group-based route guard factory.
 * Allows access when one of the user's groups grants the route (see
 * GROUP_ROUTE_ACCESS). Denied users are sent to the /unauthorized screen,
 * which offers a button to their first allowed section. Routing to a static
 * screen (rather than auto-redirecting) avoids any redirect loop.
 * Usage: canActivate: [routeAccessGuard(AppRoute.SCOUTING)]
 */
export function routeAccessGuard(route: AppRoute): CanActivateFn {
  return () => {
    const authService = inject(AuthService);
    const themeService = inject(ThemeService);
    const router = inject(Router);

    return authService.getUserData().pipe(
      take(1),
      map((userData: UserData | null) => {
        if (!userData) {
          return router.createUrlTree(['/login']);
        }

        const userGroups = userData.groups || [];

        if (canAccessRoute(userGroups, route, themeService.currentTeamId())) {
          return true;
        }

        console.warn('User group does not grant access to route', { userGroups, route });
        return router.createUrlTree(['/unauthorized']);
      })
    );
  };
}
